import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, Sparkles, Loader2, Calendar, BookOpen, MessageSquare, Video, Headphones, ListChecks } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const activityTypes = [
  { value: "reading", label: "Reading", icon: BookOpen },
  { value: "reflection", label: "Reflection", icon: MessageSquare },
  { value: "task", label: "Task/Assignment", icon: ListChecks },
  { value: "quiz", label: "Quiz", icon: ListChecks },
  { value: "video", label: "Video", icon: Video },
  { value: "audio", label: "Audio", icon: Headphones },
];

export default function ActivityEditor({ lessonIndex, lessonTitle, lessonContent, activities, setActivities }) {
  const [generating, setGenerating] = useState(false);
  const [numDays, setNumDays] = useState(5);

  const addActivity = () => {
    const newActivity = {
      day_number: activities.length + 1,
      activity_type: "task",
      title: "",
      description: "",
      content: "",
      url: ""
    };
    setActivities([...activities, newActivity]);
  };

  const removeActivity = (index) => {
    setActivities(activities.filter((_, i) => i !== index));
  };

  const updateActivity = (index, field, value) => {
    const updated = [...activities];
    updated[index][field] = value;
    setActivities(updated);
  };

  const generateWithAI = async () => {
    if (!lessonTitle || !lessonContent) {
      toast.error("Please add lesson title and content first");
      return;
    }
    
    setGenerating(true);
    try {
      const response = await fetch(`${API}/activities/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          course_id: "temp",
          lesson_index: lessonIndex,
          lesson_title: lessonTitle,
          lesson_content: lessonContent,
          num_days: numDays
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setActivities(data.activities);
        toast.success(`Generated ${data.activities.length} activities!`);
      } else {
        toast.error(data.detail || "Failed to generate activities");
      }
    } catch (error) {
      toast.error("Failed to generate activities");
    } finally {
      setGenerating(false);
    }
  };

  const getActivityIcon = (type) => {
    const actType = activityTypes.find(t => t.value === type);
    return actType ? actType.icon : ListChecks;
  };

  return (
    <div className="mt-4 p-4 bg-[#E9EDC9]/50 rounded-xl border border-[#CCD5AE]">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#3A5A40]" />
          <h4 className="font-semibold text-[#344E41]">Daily Activities</h4>
          <span className="text-sm text-[#588157]">({activities.length} activities)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Label className="text-xs text-[#588157]">Days:</Label>
            <Input
              type="number"
              min="1"
              max="14"
              value={numDays}
              onChange={(e) => setNumDays(parseInt(e.target.value) || 5)}
              className="w-16 h-8 text-sm bg-white/50 border-[#CCD5AE]"
            />
          </div>
          <Button
            type="button"
            size="sm"
            onClick={generateWithAI}
            disabled={generating}
            data-testid={`generate-activities-${lessonIndex}`}
            className="bg-gradient-to-r from-[#588157] to-[#3A5A40] text-white hover:opacity-90 text-xs"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-1" />
            )}
            AI Generate
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={addActivity}
            data-testid={`add-activity-${lessonIndex}`}
            className="bg-[#D4A373] text-white hover:bg-[#C89563] text-xs"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {activities.length === 0 ? (
        <p className="text-center text-sm text-[#588157] py-4">
          No activities yet. Add manually or use AI to generate activities.
        </p>
      ) : (
        <div className="space-y-3">
          {activities.map((activity, index) => {
            const IconComponent = getActivityIcon(activity.activity_type);
            return (
              <Card key={index} className="p-4 bg-white/70 border-[#CCD5AE]/50" data-testid={`activity-${lessonIndex}-${index}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#3A5A40]/10 rounded-lg flex items-center justify-center">
                    <span className="text-sm font-bold text-[#3A5A40]">D{activity.day_number}</span>
                  </div>
                  
                  <div className="flex-1 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-[#588157]">Day Number</Label>
                        <Input
                          type="number"
                          min="1"
                          value={activity.day_number}
                          onChange={(e) => updateActivity(index, 'day_number', parseInt(e.target.value) || 1)}
                          className="h-8 text-sm bg-white/50 border-[#CCD5AE]"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#588157]">Type</Label>
                        <Select value={activity.activity_type} onValueChange={(val) => updateActivity(index, 'activity_type', val)}>
                          <SelectTrigger className="h-8 text-sm bg-white/50 border-[#CCD5AE]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {activityTypes.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                <div className="flex items-center gap-2">
                                  <type.icon className="w-4 h-4" />
                                  {type.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs text-[#588157]">Title</Label>
                      <Input
                        value={activity.title}
                        onChange={(e) => updateActivity(index, 'title', e.target.value)}
                        placeholder="Activity title"
                        className="h-8 text-sm bg-white/50 border-[#CCD5AE]"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-xs text-[#588157]">Description</Label>
                      <Textarea
                        value={activity.description}
                        onChange={(e) => updateActivity(index, 'description', e.target.value)}
                        placeholder="What should students do?"
                        rows={2}
                        className="text-sm bg-white/50 border-[#CCD5AE]"
                      />
                    </div>

                    {(activity.activity_type === 'video' || activity.activity_type === 'audio') && (
                      <div>
                        <Label className="text-xs text-[#588157]">URL</Label>
                        <Input
                          value={activity.url || ""}
                          onChange={(e) => updateActivity(index, 'url', e.target.value)}
                          placeholder="https://..."
                          className="h-8 text-sm bg-white/50 border-[#CCD5AE]"
                        />
                      </div>
                    )}
                  </div>
                  
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeActivity(index)}
                    className="text-[#588157] hover:text-red-500 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
